import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Upload, FileText, Trash2, Plus, Utensils, Download, AlertTriangle, RotateCw, Wand2, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const MEALS = [
  { k: "welcome", l: "Welcome Drinks" }, { k: "breakfast", l: "Breakfast" }, { k: "brunch", l: "Brunch" },
  { k: "lunch", l: "Lunch" }, { k: "hi_tea", l: "Hi-Tea" }, { k: "snacks", l: "Snacks" },
  { k: "dinner", l: "Dinner" }, { k: "late_night", l: "Late Night" }, { k: "all_day", l: "All Day" }, { k: "other", l: "Other" },
];
const MEAL_ORDER = Object.fromEntries(MEALS.map((m, i) => [m.k, i]));
const mealLabel = (k) => MEALS.find((m) => m.k === k)?.l || "All Day";
const fmtDay = (sec) => {
  if (sec.date) {
    const d = new Date(sec.date + "T00:00:00");
    if (!isNaN(d)) return d.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
    return sec.date;
  }
  return sec.date_label || "";
};

const DIET = [
  { k: "veg", l: "Veg", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { k: "non_veg", l: "Non-veg", c: "bg-red-50 text-red-700 border-red-200" },
  { k: "vegan", l: "Vegan", c: "bg-lime-50 text-lime-700 border-lime-200" },
  { k: "jain", l: "Jain", c: "bg-amber-50 text-amber-700 border-amber-200" },
  { k: "egg", l: "Egg", c: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  { k: "contains_nuts", l: "Nuts", c: "bg-orange-50 text-orange-700 border-orange-200" },
  { k: "spicy", l: "Spicy", c: "bg-rose-50 text-rose-700 border-rose-200" },
  { k: "chef_special", l: "Chef's", c: "bg-violet-50 text-violet-700 border-violet-200" },
];
const diet = (k) => DIET.find((d) => d.k === k) || { k, l: k, c: "bg-zinc-100 text-zinc-600 border-zinc-200" };

export const BookingMenuDialog = ({ open, onOpenChange, booking, onChanged }) => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("bookings_menu");
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const [mn, setMn] = useState(null);
  const [sections, setSections] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  const apply = (m) => { setMn(m); setSections((m?.sections || []).map((s) => ({ ...s, items: (s.items || []).map((i) => ({ ...i })) }))); };

  const fetchMenu = async () => {
    try {
      const { data } = await api.get(`/bookings/${booking.id}/menu`);
      apply(data && data.status !== "none" ? data : null);
      if (data?.status === "processing") startPoll(); else stopPoll();
    } catch { /* ignore */ }
  };
  const startPoll = () => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/bookings/${booking.id}/menu`);
        if (data?.status !== "processing") { apply(data); stopPoll(); onChanged?.(); }
      } catch { /* ignore */ }
    }, 3000);
  };

  useEffect(() => {
    if (open && booking) {
      apply(booking.menu && booking.menu.status !== "none" ? booking.menu : null);
      if (booking.menu?.status === "processing") startPoll(); else fetchMenu();
    }
    return stopPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, booking?.id]);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/bookings/${booking.id}/menu`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      apply(data.menu);
      toast.success("Menu uploaded — AI is reading every item…");
      startPoll();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const reanalyze = async () => {
    setUploading(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/menu/analyze`);
      apply(data.menu); toast.success("Re-reading the menu…"); startPoll(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not analyse"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/bookings/${booking.id}/menu/sections`, { sections });
      apply(data.menu); toast.success("Menu saved"); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const removeMenu = async () => {
    try { await api.delete(`/bookings/${booking.id}/menu`); apply(null); toast.success("Menu removed"); onChanged?.(); }
    catch { toast.error("Could not remove"); }
  };

  const downloadOriginal = async () => {
    try {
      const resp = await api.get(`/bookings/${booking.id}/menu/file`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data); window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Could not open file"); }
  };

  // editing helpers
  const setSec = (si, patch) => setSections((arr) => arr.map((s, i) => i === si ? { ...s, ...patch } : s));
  const delSec = (si) => setSections((arr) => arr.filter((_, i) => i !== si));
  const addSec = () => setSections((arr) => [...arr, { name: "New section", meal: "all_day", date: null, date_label: null, items: [{ name: "", dietary: [] }] }]);
  const setItem = (si, ii, patch) => setSections((arr) => arr.map((s, i) => i !== si ? s : { ...s, items: s.items.map((it, j) => j === ii ? { ...it, ...patch } : it) }));
  const delItem = (si, ii) => setSections((arr) => arr.map((s, i) => i !== si ? s : { ...s, items: s.items.filter((_, j) => j !== ii) }));
  const addItem = (si) => setSections((arr) => arr.map((s, i) => i !== si ? s : { ...s, items: [...s.items, { name: "", dietary: [] }] }));
  const toggleTag = (si, ii, tag) => setSections((arr) => arr.map((s, i) => i !== si ? s : {
    ...s, items: s.items.map((it, j) => j !== ii ? it : { ...it, dietary: (it.dietary || []).includes(tag) ? it.dietary.filter((t) => t !== tag) : [...(it.dietary || []), tag] }),
  }));

  const status = mn?.status;
  const totalItems = sections.reduce((n, s) => n + (s.items?.length || 0), 0);

  // Group sections by day (date or date_label), keeping original indices for
  // editing, and order meals within each day by natural serving order.
  const dayGroups = (() => {
    const order = [];
    const map = new Map();
    sections.forEach((s, si) => {
      const key = (s.date || "") + "|" + (s.date_label || "");
      if (!map.has(key)) { map.set(key, { label: fmtDay(s), list: [] }); order.push(key); }
      map.get(key).list.push({ s, si });
    });
    order.forEach((k) => map.get(k).list.sort(
      (a, b) => (MEAL_ORDER[a.s.meal ?? "all_day"] ?? 99) - (MEAL_ORDER[b.s.meal ?? "all_day"] ?? 99)
    ));
    return { order, map };
  })();
  const singleUnlabeled = dayGroups.order.length === 1 && !dayGroups.map.get(dayGroups.order[0])?.label;

  const renderSection = (s, si) => (
    <div key={s.id || si} className="border border-zinc-200 rounded-md overflow-hidden" data-testid={`menu-section-${si}`}>
      <div className="flex items-center gap-2 bg-zinc-50 px-3 py-2 border-b border-zinc-200 flex-wrap">
        {canManage ? (
          <>
            <Input value={s.name} onChange={(e) => setSec(si, { name: e.target.value })} className="h-7 text-sm font-semibold border-0 bg-transparent px-0 focus-visible:ring-0 flex-1 min-w-[120px]" data-testid={`menu-section-name-${si}`} />
            <select value={s.meal || "all_day"} onChange={(e) => setSec(si, { meal: e.target.value })} className="h-7 text-xs rounded border border-zinc-200 bg-white px-1.5 text-zinc-600" data-testid={`menu-section-meal-${si}`}>
              {MEALS.map((m) => <option key={m.k} value={m.k}>{m.l}</option>)}
            </select>
            <Input type="date" value={s.date || ""} onChange={(e) => setSec(si, { date: e.target.value || null, date_label: e.target.value ? null : s.date_label })} className="h-7 text-xs w-[140px]" data-testid={`menu-section-date-${si}`} />
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => addItem(si)} data-testid={`menu-add-item-${si}`}><Plus className="w-3.5 h-3.5" /></Button>
            <button onClick={() => delSec(si)} className="text-zinc-400 hover:text-red-600" data-testid={`menu-del-section-${si}`}><Trash2 className="w-4 h-4" /></button>
          </>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold uppercase tracking-wide text-zinc-700">{s.name}</span>
            {s.meal && s.meal !== "all_day" && (
              <span className="text-[10px] rounded-full px-2 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 font-medium">{mealLabel(s.meal)}</span>
            )}
          </div>
        )}
      </div>
      <div className="divide-y divide-zinc-100">
        {(s.items || []).map((it, ii) => (
          <div key={it.id || ii} className="px-3 py-2" data-testid={`menu-item-${si}-${ii}`}>
            {canManage ? (
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0 space-y-1">
                  <Input value={it.name} onChange={(e) => setItem(si, ii, { name: e.target.value })} placeholder="Dish name" className="h-8 text-sm" data-testid={`menu-item-name-${si}-${ii}`} />
                  <Input value={it.description || ""} onChange={(e) => setItem(si, ii, { description: e.target.value })} placeholder="Description (optional)" className="h-7 text-xs text-zinc-500" />
                  <div className="flex flex-wrap gap-1 pt-0.5">
                    {DIET.map((d) => {
                      const on = (it.dietary || []).includes(d.k);
                      return <button key={d.k} type="button" onClick={() => toggleTag(si, ii, d.k)} className={`text-[10px] rounded-full px-2 py-0.5 border ${on ? d.c : "bg-white text-zinc-400 border-zinc-200"}`}>{d.l}</button>;
                    })}
                  </div>
                </div>
                <button onClick={() => delItem(si, ii)} className="text-zinc-400 hover:text-red-600 mt-1" data-testid={`menu-del-item-${si}-${ii}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium">{it.name}</span>
                  {(it.dietary || []).map((t) => <span key={t} className={`text-[10px] rounded-full px-1.5 py-0.5 border ${diet(t).c}`}>{diet(t).l}</span>)}
                </div>
                {it.description && <div className="text-xs text-zinc-500 mt-0.5">{it.description}</div>}
              </div>
            )}
          </div>
        ))}
        {(s.items || []).length === 0 && <div className="px-3 py-2 text-xs text-zinc-400">No items.</div>}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) stopPoll(); onOpenChange(o); }}>
      <DialogContent className="max-w-2xl" data-testid="booking-menu-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Utensils className="w-4 h-4 text-orange-500" /> Food menu · {booking?.client_name}</DialogTitle>
          <DialogDescription>Upload the menu — the AI captures every item. {canManage ? "Edit, add or remove items below." : "Read-only view for your team."}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[68vh] overflow-y-auto pr-1">
          {/* upload row */}
          <div className="flex items-center justify-between gap-3 border border-zinc-200 rounded-md p-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm min-w-0">
              <FileText className="w-4 h-4 text-zinc-400 shrink-0" />
              {mn?.file_name ? (
                <button onClick={downloadOriginal} className="truncate text-zinc-800 hover:underline flex items-center gap-1" data-testid="menu-download">{mn.file_name} <Download className="w-3 h-3" /></button>
              ) : <span className="text-zinc-400">No menu uploaded yet</span>}
            </div>
            {canManage && (
              <div className="flex gap-2">
                <input ref={fileRef} type="file" className="hidden" accept=".pdf,.txt,.docx,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.webp,.heic,.heif" onChange={(e) => doUpload(e.target.files?.[0])} data-testid="menu-file-input" />
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="menu-upload-btn">
                  {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : (mn ? <RotateCw className="w-4 h-4 mr-1" /> : <Upload className="w-4 h-4 mr-1" />)}{mn ? "Replace" : "Upload"}
                </Button>
                {mn?.file_name && <Button size="sm" variant="outline" onClick={reanalyze} disabled={uploading || status === "processing"} data-testid="menu-reanalyze"><Wand2 className="w-3.5 h-3.5" /></Button>}
                {mn && <Button size="sm" variant="ghost" className="text-red-600" onClick={removeMenu} data-testid="menu-remove"><Trash2 className="w-4 h-4" /></Button>}
              </div>
            )}
          </div>

          {status === "processing" && <div className="flex items-center gap-2 text-sm text-zinc-500 py-6 justify-center" data-testid="menu-processing"><Loader2 className="w-4 h-4 animate-spin" /> AI is reading every item…</div>}
          {status === "error" && <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3"><AlertTriangle className="w-4 h-4" /> Couldn't read this file. {mn?.error}</div>}

          {(sections.length > 0 || status === "ready") && (
            <div className="flex items-center justify-between">
              <div className="label-overline">Menu · {totalItems} items</div>
              {canManage && <Button size="sm" variant="ghost" onClick={addSec} data-testid="menu-add-section"><Plus className="w-4 h-4 mr-1" /> Section</Button>}
            </div>
          )}

          {dayGroups.order.map((gk) => {
            const g = dayGroups.map.get(gk);
            return (
              <div key={gk} className="space-y-2" data-testid={`menu-day-${gk || "general"}`}>
                {g.label && !singleUnlabeled && (
                  <div className="flex items-center gap-2 pt-1">
                    <CalendarDays className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                    <span className="text-sm font-bold text-zinc-800">{g.label}</span>
                    <div className="flex-1 h-px bg-zinc-200" />
                  </div>
                )}
                {g.list.map(({ s, si }) => renderSection(s, si))}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {canManage && (sections.length > 0 || status === "ready") && (
            <Button onClick={save} disabled={saving} className="bg-zinc-950" data-testid="menu-save">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save menu</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
